// Bottom-sheet receipt for any computed value — the P5 contract made
// touch-first. Long-press on a stat, the receipt slides up showing the
// number's full decomposition: title (value), then a list of every
// contributing source with type pill, signed contribution, and note.
//
// Two render modes:
//   - List mode (default): pass `modifiers` array of
//     `{ effect, source, sourceType?, note? }`. Renders as a vertical
//     list with type-coded pills and aligned numeric contributions.
//   - Damage-stagger mode: pass `damageBreakdown` array of
//     `{ label, value, kind? }` for sequential damage resolution
//     ("20 → 10 (resistance) → 7 (wardsave)"). Reveals at 80 ms/line
//     with opacity + slide-in choreography. Reduced-motion users get
//     all lines instantly.
//
// Why a bottom sheet rather than an inline popover: thumb zone (§5 4a).
// Sources can be 1 line or 20 lines; a sheet handles either without
// dynamic positioning math, and dismiss-by-drag-down keeps the
// gesture vocabulary consistent with the rest of the mobile chrome.
//
// Accessibility:
//   - The sheet inherits MobileBottomSheet's focus trap.
//   - Source rows are <li> in a <ul role="list"> for screen-reader
//     coherence; the sheet itself carries aria-label="<stat> sources".
//   - Type pills carry their type name as plain text, not just color,
//     to satisfy "differentiate without color alone" (CLAUDE.md P9).
//
// Theme: tokens via `var(--bg2, ...)` etc. matches the rest of the
// mobile chrome. Color coding: green for positive numeric, red for
// negative; pills inherit category color (item / buff / disease /
// system / diet / armor / base) and fall back to muted gray.

import React, { useEffect, useState } from 'react';
import MobileBottomSheet from './MobileBottomSheet';
import useReducedMotion from './useReducedMotion';

// Damage-stagger interval. 80 ms = readable as a sequence, not so slow
// that a 4-step damage takes more than half a second to fully reveal.
const STAGGER_MS = 80;

// Color codes per source category. Map keys are case-insensitive on
// match. Anything that doesn't match falls back to muted.
const PILL_COLORS = {
  base:    { bg: 'rgba(154, 139, 122, 0.18)', text: 'var(--ts, #9a8b7a)' },
  item:    { bg: 'rgba(212, 168, 50, 0.22)',  text: 'var(--tg, #d4a832)' },
  armor:   { bg: 'rgba(212, 168, 50, 0.22)',  text: 'var(--tg, #d4a832)' },
  buff:    { bg: 'rgba(125, 216, 125, 0.18)', text: 'var(--tgr, #7dd87d)' },
  diet:    { bg: 'rgba(125, 216, 125, 0.18)', text: 'var(--tgr, #7dd87d)' },
  disease: { bg: 'rgba(212, 64, 64, 0.18)',   text: 'var(--tr, #d44040)' },
  debuff:  { bg: 'rgba(212, 64, 64, 0.18)',   text: 'var(--tr, #d44040)' },
  system:  { bg: 'rgba(107, 63, 160, 0.22)',  text: 'var(--ap, #6b3fa0)' },
  curse:   { bg: 'rgba(107, 63, 160, 0.22)',  text: 'var(--ap, #6b3fa0)' },
};

const pillColors = (kind) => {
  if (!kind) return { bg: 'rgba(255,255,255,0.06)', text: 'var(--ts, #9a8b7a)' };
  return PILL_COLORS[String(kind).toLowerCase()] || { bg: 'rgba(255,255,255,0.06)', text: 'var(--ts, #9a8b7a)' };
};

// Format a numeric contribution as a signed string. Numbers get an
// explicit sign so the receipt reads "+2 / -1 / +0" rather than
// "2 / -1 / 0" (negative signs already render naturally). Strings
// pass through unmodified — callers can pre-format dice notation
// like "+1d4" or "−2d6 fire" exactly how they want it displayed.
const formatContribution = (effect) => {
  if (effect == null) return '';
  if (typeof effect === 'number') return effect >= 0 ? `+${effect}` : `${effect}`;
  return String(effect).trim();
};

const contributionColor = (effect) => {
  if (typeof effect === 'number') {
    if (effect > 0) return 'var(--tgr, #7dd87d)';
    if (effect < 0) return 'var(--tr, #d44040)';
    return 'var(--ts, #9a8b7a)';
  }
  if (typeof effect === 'string') {
    if (effect.trim().startsWith('-')) return 'var(--tr, #d44040)';
    if (effect.trim().startsWith('+')) return 'var(--tgr, #7dd87d)';
  }
  return 'var(--tp, #e8ddd0)';
};

export default function ReceiptSheet({
  open,
  onClose,
  title,
  value,
  unit = null,
  modifiers = null,
  damageBreakdown = null,
  emptyMessage = 'No active modifiers — value is at its base.',
}) {
  const reduceMotion = useReducedMotion();
  const isDamageMode = Array.isArray(damageBreakdown) && damageBreakdown.length > 0;

  // Damage-stagger reveal: how many lines are currently visible.
  const [revealedCount, setRevealedCount] = useState(0);

  useEffect(() => {
    if (!open || !isDamageMode) {
      setRevealedCount(0);
      return undefined;
    }
    if (reduceMotion) {
      setRevealedCount(damageBreakdown.length);
      return undefined;
    }
    setRevealedCount(1);
    const timers = [];
    for (let i = 1; i < damageBreakdown.length; i++) {
      timers.push(setTimeout(() => setRevealedCount(i + 1), i * STAGGER_MS));
    }
    return () => timers.forEach(clearTimeout);
  }, [open, isDamageMode, damageBreakdown, reduceMotion]);

  const ariaLabel = title ? `${title} sources` : 'Receipt';

  return (
    <MobileBottomSheet open={open} onClose={onClose} ariaLabel={ariaLabel}>
      <div style={{ padding: '4px 4px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Header title={title} value={value} unit={unit} />
        {isDamageMode
          ? <DamageBreakdownList breakdown={damageBreakdown} revealedCount={revealedCount} reduceMotion={reduceMotion} />
          : <ModifierList modifiers={modifiers} emptyMessage={emptyMessage} />}
      </div>
    </MobileBottomSheet>
  );
}

function Header({ title, value, unit }) {
  return (
    <div style={{ paddingBottom: '8px', borderBottom: '1px solid var(--bs, #2a2033)' }}>
      {title ? (
        <div style={{
          fontSize: '0.72rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '1.2px',
          color: 'var(--ts, #9a8b7a)',
          marginBottom: '4px',
        }}>
          {title}
        </div>
      ) : null}
      {value !== undefined && value !== null ? (
        <div style={{
          fontSize: '1.6rem',
          fontWeight: 700,
          color: 'var(--tg, #d4a832)',
          fontFamily: "'Cinzel', serif",
          letterSpacing: '0.5px',
          lineHeight: 1.1,
        }}>
          {value}{unit ? <span style={{ fontSize: '0.85rem', marginLeft: '4px', color: 'var(--ts, #9a8b7a)' }}>{unit}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function ModifierList({ modifiers, emptyMessage }) {
  if (!Array.isArray(modifiers) || modifiers.length === 0) {
    return (
      <div style={{
        padding: '12px 0',
        fontSize: '0.85rem',
        color: 'var(--tm, #5e5266)',
        fontStyle: 'italic',
      }}>
        {emptyMessage}
      </div>
    );
  }
  return (
    <ul role="list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {modifiers.map((mod, idx) => (
        <ModifierRow key={`${mod.source || 'mod'}-${idx}`} mod={mod} />
      ))}
    </ul>
  );
}

function ModifierRow({ mod }) {
  const kind = mod.sourceType || mod.kind || mod.note || null;
  const pill = pillColors(kind);
  const contribution = formatContribution(mod.effect);
  const contributionStyle = { color: contributionColor(mod.effect), fontWeight: 700, fontVariantNumeric: 'tabular-nums' };
  return (
    <li style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 10px',
      background: 'var(--bg1, #16101a)',
      border: '1px solid var(--bs, #2a2033)',
      borderRadius: '6px',
      minHeight: '44px',
    }}>
      {kind ? (
        <span style={{
          fontSize: '0.62rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          padding: '3px 7px',
          borderRadius: '3px',
          background: pill.bg,
          color: pill.text,
          flexShrink: 0,
        }}>
          {kind}
        </span>
      ) : null}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
        <span style={{
          fontSize: '0.85rem',
          color: 'var(--tp, #e8ddd0)',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {mod.source || mod.label || 'Unknown'}
        </span>
        {mod.note && mod.note !== kind ? (
          <span style={{
            fontSize: '0.72rem',
            color: 'var(--tm, #5e5266)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {mod.note}
          </span>
        ) : null}
      </div>
      {contribution ? (
        <span style={{ ...contributionStyle, fontSize: '0.95rem', flexShrink: 0 }}>{contribution}</span>
      ) : null}
    </li>
  );
}

function DamageBreakdownList({ breakdown, revealedCount, reduceMotion }) {
  return (
    <ol role="list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {breakdown.map((step, idx) => {
        const visible = idx < revealedCount;
        return (
          <li
            key={`step-${idx}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 10px',
              background: idx === breakdown.length - 1 ? 'rgba(212, 64, 64, 0.10)' : 'var(--bg1, #16101a)',
              border: '1px solid var(--bs, #2a2033)',
              borderRadius: '6px',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(4px)',
              transition: reduceMotion ? 'none' : 'opacity 160ms ease-out, transform 160ms ease-out',
              minHeight: '40px',
            }}
          >
            <span style={{
              fontSize: '0.62rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              color: 'var(--ts, #9a8b7a)',
              minWidth: '18px',
              textAlign: 'right',
            }}>
              {idx + 1}.
            </span>
            <span style={{
              flex: 1,
              fontSize: '0.85rem',
              color: 'var(--tp, #e8ddd0)',
              minWidth: 0,
            }}>
              {step.label}
            </span>
            <span style={{
              fontSize: '1rem',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: idx === breakdown.length - 1 ? 'var(--tr, #d44040)' : 'var(--tp, #e8ddd0)',
              flexShrink: 0,
            }}>
              {step.value}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
